'use client';

import { useBundler } from '@app/components/bundler/bundler-provider/bundler-provider';
import getCurrencySymbol from '@app/lib/helpers/get-currency-symbol';
import { type Language } from '@app/lib/store-context/Context';
import { getContext } from '@app/lib/store-context/get-context';
import { productImagesByPath, reformatImageSet } from '@lib/productImageUtils';
import cn from 'clsx';
import Image from 'next/image';
import { useParams } from 'next/navigation';
import ContentLoader from 'react-content-loader';
import { useMediaQuery } from 'react-responsive';
import styles from './BundlerSelectedProducts.module.css';
import {
    calculateDiscountedAmount,
    calculateTotalPrice,
    formatPrice,
} from './priceCalculations';

const BundlerSelectedProducts = () => {
    const { lang } = useParams();
    const context = getContext(lang as Language);
    const { currencyCode } = context;
    const {
        chosenBundleVariants,
        bundle,
        assertBundleIsComplete,
        isFetching,
        addBundleToCart,
        errorMessage,
    } = useBundler();
    const isTabletOrMobile = useMediaQuery({ query: '(max-width: 991px)' });

    if (!bundle) return null;

    if (isFetching)
        return isTabletOrMobile ? (
            <ContentLoader viewBox="0 0 100 45" height={45} width="100%">
                <rect x="6.5" y="4" rx="0" ry="0" width="25%" height="3" />
                <rect x="12.2" y="10" rx="0" ry="0" width="12%" height="36%" />
                <rect x="24.5" y="10" rx="0" ry="0" width="12%" height="36%" />
                <rect x="0" y="10" rx="0" ry="0" width="12%" height="36%" />
                <rect x="6.5" y="29" rx="5" ry="5" width="87%" height="9" />
            </ContentLoader>
        ) : (
            <ContentLoader viewBox="0 0 100 40" height={40} width="100%">
                <rect x="13" y="2" rx="0" ry="0" width="25%" height="3" />
                <rect x="13" y="8" rx="0" ry="0" width="10%" height="35%" />
                <rect x="13" y="25" rx="5" ry="5" width="75%" height="9" />
            </ContentLoader>
        );

    const { discountPercentage, collections } = bundle;
    const defaultCurrencySymbol = getCurrencySymbol(
        lang as Language,
        currencyCode
    );
    const total = calculateTotalPrice(chosenBundleVariants);
    const discountedAmount = calculateDiscountedAmount(
        total,
        discountPercentage
    );
    const afterDiscountTotalAmount = formatPrice(
        discountedAmount,
        defaultCurrencySymbol
    );
    const numberOfCollection = bundle.collections.length;

    return (
        <>
            <div className={styles.root}>
                <div
                    className={cn(
                        'mmds-component-one-detail',
                        styles.bundlerSelectedProduct
                    )}
                >
                    {Object.keys(chosenBundleVariants).length === 0 ? (
                        <div className={styles.bspInitialState}>
                            Bundle: Select Products
                        </div>
                    ) : (
                        <div className="">
                            {`Bundle: ${
                                Object.keys(chosenBundleVariants).length
                            } of ${collections.length} products`}
                            {assertBundleIsComplete() ? (
                                <span>
                                    {`,`}{' '}
                                    <span
                                        className={
                                            styles.bpiBeforeDiscountTotalAmount
                                        }
                                    >
                                        {defaultCurrencySymbol}
                                        {total.toFixed(2)}
                                    </span>{' '}
                                    {afterDiscountTotalAmount}
                                </span>
                            ) : (
                                ''
                            )}
                        </div>
                    )}
                </div>
                <div className={styles.bspImagesContainer}>
                    {Object.keys(chosenBundleVariants).map((key) => {
                        const variant = chosenBundleVariants[key];
                        const { product } = variant;
                        let productImages = product.images.edges;
                        productImages = reformatImageSet(productImages);

                        const flatlayImageExtract = productImages.find(
                            (image) => image.src.includes('_LP_FLATLAY')
                        );

                        const productImagesExtract = [
                            productImagesByPath(
                                productImages,
                                'product_card.*.*.*.flatlay'
                            ),
                        ];

                        const { src, width, height } =
                            productImagesExtract[0][0]
                                ? productImagesExtract[0][0]
                                : flatlayImageExtract;

                        return (
                            <div
                                className={
                                    numberOfCollection === 3
                                        ? styles.bspImage
                                        : styles.bspImage2
                                }
                                key={key}
                            >
                                <Image
                                    key={key}
                                    src={src ? src : flatlayImageExtract.src}
                                    width={
                                        width
                                            ? width
                                            : flatlayImageExtract.width
                                    }
                                    height={
                                        height
                                            ? height
                                            : flatlayImageExtract.height
                                    }
                                    alt="bundler-selected-product-image"
                                    style={{
                                        maxWidth: '100%',
                                        height: '100%',
                                    }}
                                />
                            </div>
                        );
                    })}
                </div>
                {!!Object.keys(chosenBundleVariants).length && (
                    <div
                        className={
                            assertBundleIsComplete()
                                ? styles.bspSelected
                                : styles.bspButton
                        }
                        onClick={addBundleToCart}
                    >
                        <button type="button">ADD BUNDLE TO CART</button>
                        {errorMessage && (
                            <p
                                className={cn(
                                    'mmds-component-two-detail',
                                    styles.errorMessage
                                )}
                            >
                                {errorMessage}
                            </p>
                        )}
                    </div>
                )}
            </div>
        </>
    );
};

export default BundlerSelectedProducts;
